// src/components/products/ProductCard.tsx
import React from 'react';
import { Star, ShoppingCart } from 'lucide-react';
import Button from '@/components/ui/Button';
import { Link } from 'react-router-dom';
import { UPLOADS_URL } from '@/utils/utils';

interface ProductCardProps {
    product: {
        id: number;
        name: string;
        slug: string;
        price: number;
        compare_price?: number;
        rating_average: number;
        rating_count: number;
        primary_image: string;
        category_name: string;
        seller_name: string;
        in_stock: boolean;
        discount_percentage: number;
    };
    showSeller?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
    product,
    showSeller = true
}) => {
    // Calcular el precio con descuento si existe
    const discountedPrice = product.discount_percentage > 0
        ? product.price * (1 - product.discount_percentage / 100)
        : null;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
            {/* Imagen del producto */}
            <Link to={`/products/${product.slug}`} className="block relative">
                <div className="aspect-square w-full bg-gray-100 overflow-hidden">
                    <img
                        src={`${UPLOADS_URL}/${product.primary_image}`}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/images/placeholder-product.jpg';
                            target.onerror = null; // Prevenir bucle infinito
                        }}
                    />
                </div>
                {/* Etiqueta de descuento si aplica */}
                {product.discount_percentage > 0 && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {product.discount_percentage}% OFF
                    </div>
                )}
                {/* Indicador de stock */}
                {!product.in_stock && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <span className="text-white font-semibold">Sin Stock</span>
                    </div>
                )}
            </Link>

            {/* Contenido de la tarjeta */}
            <div className="p-4">
                {/* Información del vendedor */}
                {showSeller && product.seller_name && (
                    <div className="mb-1">
                        <p className="text-xs text-gray-500 truncate">
                            Vendedor: {product.seller_name}
                        </p>
                    </div>
                )}

                {/* Categoría */}
                <div className="mb-1">
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                        {product.category_name}
                    </span>
                </div>

                {/* Nombre del producto */}
                <Link to={`/products/${product.slug}`} className="block">
                    <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 hover:text-primary-600 transition-colors min-h-[2.5rem]">
                        {product.name}
                    </h3>
                </Link>

                {/* Precio */}
                <div className="mt-2">
                    {discountedPrice ? (
                        <div className="flex items-baseline">
                            <span className="text-lg font-bold text-gray-900">
                                ${discountedPrice.toFixed(2)}
                            </span>
                            <span className="ml-2 text-sm text-gray-500 line-through">
                                ${product.price.toFixed(2)}
                            </span>
                        </div>
                    ) : (
                        <span className="text-lg font-bold text-gray-900">
                            ${product.price.toFixed(2)}
                        </span>
                    )}
                </div>

                {/* Valoraciones */}
                <div className="mt-2 flex items-center">
                    <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="ml-1 text-sm text-gray-600">
                            {product.rating_average.toFixed(1)}
                        </span>
                    </div>
                    <span className="mx-1 text-gray-300">•</span>
                    <span className="text-sm text-gray-500">
                        {product.rating_count} {product.rating_count === 1 ? 'reseña' : 'reseñas'}
                    </span>
                </div>

                {/* Botón de añadir al carrito */}
                <Button
                    variant="primary"
                    size="sm"
                    className="w-full mt-4 flex items-center justify-center"
                    leftIcon={<ShoppingCart className="w-4 h-4" />}
                    disabled={!product.in_stock}
                >
                    {product.in_stock ? 'Añadir al carrito' : 'Sin Stock'}
                </Button>
            </div>
        </div>
    );
};

export default ProductCard;